<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useUserStore } from '@/stores/user'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'
import CreateBridgeCustomerForm from './CreateBridgeCustomerForm.vue'
import CreateTransferForm from './CreateTransferForm.vue'
import BridgeCombinedList from './BridgeCombinedList.vue'
import BridgeTransfersList from './BridgeTransfersList.vue'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { API, HOST } from '@/utils/http'
import type { BridgeCustomer, BridgeOrganization, CombinedCustomer, BridgeTransfer } from '@/types/bridge'

const userStore = useUserStore()
const member = computed(() => userStore.member)

// Active tab
const activeTab = ref<'customers' | 'transfers'>('customers')

// Customers state
const customers = ref<BridgeCustomer[]>([])
const customersLoading = ref(false)
const customersError = ref<string | null>(null)

// Organizations state
const organizations = ref<BridgeOrganization[]>([])
const organizationsLoading = ref(false)
const organizationsError = ref<string | null>(null)

// Transfers state
const transfers = ref<BridgeTransfer[]>([])
const transfersLoading = ref(false)
const transfersError = ref<string | null>(null)

// Metrics
const kybComplete = computed(() =>
  organizations.value.filter(o => o.tos_status === 'approved' && o.kyc_status === 'approved').length
)
const kycComplete = computed(() =>
  customers.value.filter(c => c.tos_status === 'approved' && c.kyc_status === 'approved').length
)
const completedTransfers = computed(() =>
  transfers.value.filter(t => t.status === 'payment_processed')
)
const paymentVolume = computed(() =>
  completedTransfers.value.reduce((sum, t) => sum + parseFloat(t.amount || '0'), 0)
)
const feesMade = computed(() => paymentVolume.value * 0.0025)

const formatCurrency = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })

// Combined loading state
const isLoading = computed(() => customersLoading.value || organizationsLoading.value)

// Combined error
const error = computed(() => customersError.value || organizationsError.value || transfersError.value)

// Combined customers list
const combinedCustomers = computed<CombinedCustomer[]>(() => {
  const individuals = customers.value.map(c => ({
    type: 'individual' as const,
    id: c.id,
    bridge_customer_id: c.bridge_customer_id,
    name: c.member_email || 'Unknown',
    kyc_status: c.kyc_status,
    tos_status: c.tos_status,
    created_at: c.created_at,
    link: c.link
  }))

  const businesses = organizations.value.map(o => ({
    type: 'business' as const,
    id: o.id,
    bridge_customer_id: o.bridge_customer_id,
    name: o.organization_name || `Org #${o.organization_id}`,
    contactName: o.contact_first_name && o.contact_last_name
      ? `${o.contact_first_name} ${o.contact_last_name}`
      : undefined,
    contactEmail: o.contact_email || undefined,
    kyc_status: o.kyc_status,
    tos_status: o.tos_status,
    created_at: o.created_at,
    link: o.link
  }))

  return [...individuals, ...businesses]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
})

const fetchCustomers = async () => {
  customersLoading.value = true
  customersError.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/payments/bridge/customers`
    })
    customers.value = response.data
  } catch (err: any) {
    console.error('Error fetching Bridge customers:', err)
    customersError.value = err.response?.data?.detail || 'Failed to fetch customers'
  } finally {
    customersLoading.value = false
  }
}

const fetchOrganizations = async () => {
  organizationsLoading.value = true
  organizationsError.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/payments/bridge/organizations`
    })
    organizations.value = response.data
  } catch (err: any) {
    console.error('Error fetching Bridge organizations:', err)
    organizationsError.value = err.response?.data?.detail || 'Failed to fetch organizations'
  } finally {
    organizationsLoading.value = false
  }
}

const fetchTransfers = async () => {
  transfersLoading.value = true
  transfersError.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/payments/bridge/transfers`,
      params: { limit: 100 }
    })
    transfers.value = response.data
  } catch (err: any) {
    console.error('Error fetching Bridge transfers:', err)
    transfersError.value = err.response?.data?.detail || 'Failed to fetch transfers'
  } finally {
    transfersLoading.value = false
  }
}

const handleCustomerCreated = () => {
  fetchCustomers()
  fetchOrganizations()
}

const handleTransferCreated = (transfer: BridgeTransfer) => {
  transfers.value = [transfer, ...transfers.value]
  activeTab.value = 'transfers'
}

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible' && activeTab.value === 'transfers' && !transfersLoading.value) {
    fetchTransfers()
  }
}

watch(activeTab, (tab) => {
  if (tab === 'transfers' && !transfersLoading.value) fetchTransfers()
})

onMounted(() => {
  fetchCustomers()
  fetchOrganizations()
  fetchTransfers()
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
})
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />

    <div class="p-6 space-y-4 flex-1 overflow-auto">
      <!-- Metrics bar -->
      <div class="grid grid-cols-5 gap-3">
        <div v-for="metric in [
          { label: 'KYB Complete', value: kybComplete },
          { label: 'KYC Complete', value: kycComplete },
          { label: 'Total Transfers', value: completedTransfers.length },
          { label: 'Payment Volume', value: formatCurrency(paymentVolume) },
          { label: 'Fees Made', value: formatCurrency(feesMade) },
        ]" :key="metric.label" class="rounded-lg border bg-card p-4">
          <div class="text-xs text-muted-foreground mb-1">{{ metric.label }}</div>
          <div class="text-2xl font-semibold">{{ metric.value }}</div>
        </div>
      </div>

      <!-- Error display -->
      <div v-if="error" class="p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm">
        {{ error }}
      </div>

      <!-- Tabs for Customers and Transfers -->
      <Tabs v-model="activeTab" class="w-full">
        <div class="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="customers">Customers</TabsTrigger>
            <TabsTrigger value="transfers">Transfers</TabsTrigger>
          </TabsList>
          <CreateBridgeCustomerForm v-if="activeTab === 'customers'" @customer-created="handleCustomerCreated" />
          <CreateTransferForm v-if="activeTab === 'transfers'" @transfer-created="handleTransferCreated" />
        </div>

        <!-- Customers Tab -->
        <TabsContent value="customers" class="mt-4">
          <div class="text-sm text-muted-foreground mb-4">
            Total customers: {{ combinedCustomers.length }}
          </div>
          <BridgeCombinedList
            :customers="combinedCustomers"
            :loading="isLoading"
            @customer-updated="handleCustomerCreated"
          />
        </TabsContent>

        <!-- Transfers Tab -->
        <TabsContent value="transfers" class="mt-4">
          <div class="text-sm text-muted-foreground mb-4">Total: {{ transfers.length }}</div>
          <BridgeTransfersList :transfers="transfers" :loading="transfersLoading" />
        </TabsContent>
      </Tabs>
    </div>
  </div>
</template>
